/* =============================================================================
 * Search Page (srpfrd01)
 *
 * Full search results page using Spectrum.search SDK. Reads ?q= from the URL
 * on load, executes search, and renders a product grid with faceted filters,
 * sorting, and configurable pagination (numbered / infinite_scroll).
 *
 * Mini-PDP follows the shared initMiniPdp pattern from v5vphgd8 — the modal
 * DOM is SSR'd in Liquid with data-spectrum-pdp-* attributes, and JS populates
 * it from per-card search result data on open.
 *
 * SDK dependencies (resolved lazily at call time):
 *   - Spectrum.search  (query, getFacets)
 *   - Spectrum.cart    (add, get)
 *   - Spectrum.money   (format)
 *   - Spectrum.products (getByHandle — for mini PDP image gallery)
 * ============================================================================= */

;(() => {
  if (window.__sai_srpfrd01_initialized__) return
  window.__sai_srpfrd01_initialized__ = true

  const SNIPPET_ID = 'srpfrd01'
  const TAG = 'sai-srpfrd01'
  const FEATURE_SLUG = 'search_plp'
  const MIN_SPINNER_MS = 350

  const SORT_OPTIONS = [
    { value: '', label: 'Relevance' },
    { value: 'price:asc', label: 'Price: Low to High' },
    { value: 'price:desc', label: 'Price: High to Low' },
    { value: 'title:asc', label: 'Title: A-Z' },
    { value: 'title:desc', label: 'Title: Z-A' },
    { value: 'created_at:desc', label: 'Newest' },
  ]

  function noopTrack() {}

  function searchApi() {
    return window.Spectrum?.search ?? null
  }

  function cartApi() {
    return window.Spectrum?.cart ?? null
  }

  function productsApi() {
    return window.Spectrum?.products ?? null
  }

  function formatMoney(cents) {
    if (cents == null) return ''
    // Prices are rupee-denominated. `Spectrum.money.format` on this brand returns
    // a bare number with no currency symbol, so format as INR directly — Indian
    // digit grouping, no decimals (e.g. 2599900 → "₹25,999").
    try {
      return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 0,
      }).format(cents / 100)
    } catch {
      return window.Spectrum?.money?.format(cents) ?? ''
    }
  }

  function escAttr(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
  }

  function fieldToFilterParam(field) {
    if (field.startsWith('variant_options.'))
      return `filter.v.o.${field.slice('variant_options.'.length)}`
    if (field.startsWith('product_indexed_metafields.')) {
      const rest = field.slice('product_indexed_metafields.'.length).split('.')
      if (rest.length >= 2) return `filter.p.m.${rest[0]}.${rest.slice(1).join('.')}`
    }
    if (field.startsWith('variant_indexed_metafields.')) {
      const rest = field.slice('variant_indexed_metafields.'.length).split('.')
      if (rest.length >= 2) return `filter.v.m.${rest[0]}.${rest.slice(1).join('.')}`
    }
    return `filter.v.${field}`
  }

  // ── Filter value helpers ──
  // A single filter param can carry multiple OR'd values, comma-joined
  // (e.g. `filter.v.o.color=Red,Black`). The backend splits on commas, so the
  // UI treats every list facet as multi-select.

  function parseFilterValues(raw) {
    if (typeof raw !== 'string' || !raw) return []
    return raw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
  }

  function valueInFilter(map, key, value) {
    return parseFilterValues(map[key]).includes(String(value))
  }

  // Add/remove a single value from a comma-joined filter param. Mutates `map`.
  function toggleFilterValue(map, key, value, on) {
    const cur = parseFilterValues(map[key])
    const idx = cur.indexOf(String(value))
    if (on && idx === -1) cur.push(String(value))
    else if (!on && idx !== -1) cur.splice(idx, 1)
    if (cur.length) map[key] = cur.join(',')
    else delete map[key]
  }

  // ── Facet normalization ──
  // The facets endpoint returns a richer shape than the snippet renders from.
  // Normalize both the current shape and the legacy
  // (field/label/displayType/values[{value,count}]) shape into one canonical
  // model the renderers consume:
  //   { field, label, kind: 'range'|'list', control, min, max, unit, position,
  //     values: [{ value, count, swatchHex, imageGid, text }] }

  function normalizeFacet(f) {
    if (!f || !f.field) return null
    const label = f.label || f.field
    const display = f.display || {}
    const position =
      typeof display.position === 'number'
        ? display.position
        : typeof f.facetPosition === 'number'
          ? f.facetPosition
          : 0

    const isRange =
      f.kind === 'range' || f.displayType === 'range' || f.displayType === 'range_slider'

    if (isRange) {
      let min = typeof f.min === 'number' ? f.min : null
      let max = typeof f.max === 'number' ? f.max : null
      // Legacy shape carries no explicit bounds — derive from value buckets.
      if ((min === null || max === null) && Array.isArray(f.values) && f.values.length) {
        const nums = f.values.map((v) => Number.parseFloat(v.value)).filter((n) => !Number.isNaN(n))
        if (nums.length) {
          min = Math.floor(Math.min(...nums))
          max = Math.ceil(Math.max(...nums))
        }
      }
      if (min === null || max === null) return null
      return {
        field: f.field,
        label,
        kind: 'range',
        control: 'range',
        min,
        max,
        unit: display.unit || null,
        position,
        values: [],
      }
    }

    const rawValues = Array.isArray(f.values) ? f.values : []
    if (!rawValues.length) return null

    const dtype = f.displayType || 'checkbox'
    // Value-display (swatch | image) drives a swatch grid, separate from the
    // control type (checkbox | range | toggle). Detect it wherever the response
    // exposes it: a field-level valueDisplayKind, the (currently buggy) root
    // displayType, or the per-value displayConfig.kind. fieldDisplayKind also
    // tells the renderer which placeholder to use for values lacking config.
    let fieldDisplayKind = null
    if (display.valueDisplayKind === 'swatch' || display.valueDisplayKind === 'image') {
      fieldDisplayKind = display.valueDisplayKind
    } else if (dtype === 'image') {
      fieldDisplayKind = 'image'
    } else if (dtype === 'swatch' || dtype === 'color_swatches') {
      fieldDisplayKind = 'swatch'
    } else {
      const cfg = rawValues.find(
        (v) =>
          v && v.displayConfig && (v.displayConfig.kind === 'swatch' || v.displayConfig.kind === 'image'),
      )
      fieldDisplayKind = cfg ? cfg.displayConfig.kind : null
    }
    let control = 'checkbox'
    if (dtype === 'toggle') control = 'toggle'
    else if (fieldDisplayKind) control = 'swatch'

    const values = rawValues.map((v) => {
      const dc = v.displayConfig || null
      const dcVal = dc?.value ?? null
      return {
        value: String(v.value),
        count: typeof v.count === 'number' ? v.count : 0,
        swatchHex: dc && dc.kind === 'swatch' ? dcVal?.hexCode || null : null,
        imageGid: dc && dc.kind === 'image' ? dcVal?.imageGid || null : null,
        // Resolved CDN URL for image-display values (the response provides a
        // ready-to-render imageUrl). A value without one gets a placeholder.
        imageUrl: dc && dc.kind === 'image' ? dcVal?.imageUrl || null : null,
        text: dc && dc.kind === 'text' ? dcVal?.text || null : null,
      }
    })

    const valueSort = display.valueSort || null
    if (valueSort === 'alpha_asc') values.sort((a, b) => a.value.localeCompare(b.value))
    else if (valueSort === 'count_desc') values.sort((a, b) => b.count - a.count)
    // 'manual' / null → preserve server order

    return {
      field: f.field,
      label,
      kind: 'list',
      control,
      valueDisplayKind: fieldDisplayKind,
      position,
      values,
    }
  }

  function productHandleFromUrl(url) {
    if (typeof url !== 'string') return null
    const m = url.match(/\/products\/([^/?#]+)/)
    return m?.[1] ? m[1] : null
  }

  // ── Discount helpers (ported from v5vphgd8) ──

  function discountPercent(price, compareAt) {
    const numPrice = Number.parseFloat(String(price ?? '').replace(/[^0-9.-]/g, ''))
    const numCompare = Number.parseFloat(String(compareAt ?? '').replace(/[^0-9.-]/g, ''))
    if (!Number.isFinite(numPrice) || !Number.isFinite(numCompare)) return null
    if (numCompare <= 0 || numCompare <= numPrice) return null
    return Math.round(((numCompare - numPrice) / numCompare) * 100)
  }

  function formatDiscount(price, compareAt, format) {
    if (format === 'amount') {
      const numPrice = Number.parseFloat(String(price ?? '').replace(/[^0-9.-]/g, ''))
      const numCompare = Number.parseFloat(String(compareAt ?? '').replace(/[^0-9.-]/g, ''))
      if (!Number.isFinite(numPrice) || !Number.isFinite(numCompare)) return null
      const diff = numCompare - numPrice
      if (!Number.isFinite(diff) || diff <= 0) return null
      const prefixMatch = String(compareAt ?? '').match(/^[^0-9-]+/)
      const prefix = prefixMatch ? prefixMatch[0].trim() : ''
      const amountStr = Math.round(diff).toLocaleString()
      return prefix ? `SAVE ${prefix}${amountStr}` : `SAVE ${amountStr}`
    }
    const pct = discountPercent(price, compareAt)
    return pct === null || pct <= 0 ? null : `${pct}% Off`
  }

  // ── Cart helpers (ported from v5vphgd8) ──

  function discoverCartSectionIds() {
    const seen = new Set()
    const elements = document.querySelectorAll(
      'cart-items-component[data-section-id],' +
        ' cart-drawer-component[data-section-id],' +
        ' cart-icon-component[data-section-id],' +
        ' cart-bubble-component[data-section-id],' +
        ' cart-notification[data-section-id],' +
        ' cart-count-bubble[data-section-id],' +
        ' [data-cart-items-section-id],' +
        ' [data-cart-drawer-section-id],' +
        ' [data-section-id^="cart"],' +
        ' [data-section-id$="cart"],' +
        ' [data-section-id*="cart-icon"],' +
        ' [data-section-id*="cart-drawer"],' +
        ' [data-section-id*="cart-bubble"]',
    )
    for (const el of elements) {
      const id =
        el.dataset?.sectionId ??
        el.getAttribute?.('data-cart-items-section-id') ??
        el.getAttribute?.('data-cart-drawer-section-id')
      if (id) seen.add(id)
    }
    if (seen.size === 0) return null
    return Array.from(seen).join(',')
  }

  function applyCartSectionUpdates(sectionsHtml) {
    if (!sectionsHtml || typeof sectionsHtml !== 'object') return
    const parser = new DOMParser()
    for (const [sectionId, html] of Object.entries(sectionsHtml)) {
      if (typeof html !== 'string' || !html) continue
      const targets = document.querySelectorAll(`[data-section-id="${CSS.escape(sectionId)}"]`)
      if (targets.length === 0) continue
      const doc = parser.parseFromString(html, 'text/html')
      const source =
        doc.querySelector(`[data-section-id="${CSS.escape(sectionId)}"]`) ??
        doc.body.firstElementChild
      if (!source) continue
      for (const target of targets) {
        target.innerHTML = source.innerHTML
      }
    }
  }

  async function refreshCartCountBadges() {
    let count
    try {
      const sdk = window.Spectrum
      const cart =
        sdk?.cart?.get != null ? await sdk.cart.get() : await (await fetch('/cart.js')).json()
      count = cart?.item_count
    } catch {
      return
    }
    if (typeof count !== 'number') return
    const text = String(count)
    const selectors = [
      '[data-cart-count]',
      '.cart-count',
      '.cart-count-bubble',
      '#cart-icon-bubble [aria-hidden="true"]',
      '.header__icon--cart .cart-count-bubble span:first-child',
    ]
    for (const sel of selectors) {
      for (const el of document.querySelectorAll(sel)) {
        if (el.querySelector(':scope > *')) continue
        el.textContent = text
      }
    }
  }

  function notifyCartUpdate({ variantId, productId, response }) {
    const sections = response?.sections ?? {}
    applyCartSectionUpdates(sections)
    refreshCartCountBadges()

    const detail = {
      resource: response ?? {},
      sourceId: String(variantId),
      data: {
        source: 'spectrum-search-plp',
        sections,
        itemCount: 1,
        productId: productId ?? undefined,
        variantId: String(variantId),
      },
    }
    try {
      document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail }))
      document.dispatchEvent(new CustomEvent('cart:added', { bubbles: true, detail }))
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail }))
    } catch {}

    const candidates = document.querySelectorAll(
      'cart-drawer-component, cart-drawer, cart-notification, [data-cart-drawer], #cart-drawer',
    )
    for (const el of candidates) {
      if (typeof el.showDialog === 'function') {
        try {
          el.showDialog()
          return
        } catch {}
      }
      if (typeof el.open === 'function') {
        try {
          el.open()
          return
        } catch {}
      }
    }
    const toggle = document.querySelector('[data-cart-toggle], [data-action="open-cart"]')
    if (toggle && typeof toggle.click === 'function') {
      try {
        toggle.click()
      } catch {}
    }
  }

  async function addVariantToCart(variantId, quantity) {
    const sdk = window.Spectrum
    const sections = discoverCartSectionIds()
    const items = { id: variantId, quantity }
    if (sdk?.cart && typeof sdk.cart.add === 'function') {
      return sdk.cart.add(items, sections ? { sections } : undefined)
    }
    const fd = new FormData()
    fd.append('id', String(variantId))
    fd.append('quantity', String(quantity))
    if (sections) fd.append('sections', sections)
    const res = await fetch('/cart/add.js', { method: 'POST', body: fd })
    if (!res.ok) {
      const text = await res.text().catch(() => '')
      throw new Error(`Cart add failed: ${res.status} ${text.slice(0, 200)}`)
    }
    return res.json().catch(() => ({}))
  }

  // ── initMiniPdp (ported from v5vphgd8) ──
  // Initialise the inline Mini-PDP modal scoped to a snippet container.
  // On open, populates gallery / variants / price / qty / description from
  // a payload object built from search results.

  function initMiniPdp(node, track) {
    const modal = node.querySelector('[data-spectrum-pdp-modal]')
    if (!modal) return null
    const trackFn = typeof track === 'function' ? track : noopTrack

    const cfgTag = modal.querySelector('[data-spectrum-pdp-config]')
    let cfg = {}
    try {
      cfg = JSON.parse(cfgTag?.textContent || '{}')
    } catch {}

    const backdrop = modal.querySelector('[data-spectrum-pdp-backdrop]')
    const closeBtn = modal.querySelector('[data-spectrum-pdp-close]')
    const trackEl = modal.querySelector('[data-spectrum-pdp-track]')
    const dotsEl = modal.querySelector('[data-spectrum-pdp-dots]')
    const chevronPrev = modal.querySelector('[data-spectrum-pdp-chevron="prev"]')
    const chevronNext = modal.querySelector('[data-spectrum-pdp-chevron="next"]')
    const titleEl = modal.querySelector('[data-spectrum-pdp-title]')
    const priceCurrentEl = modal.querySelector('[data-spectrum-pdp-price-current]')
    const priceMrpEl = modal.querySelector('[data-spectrum-pdp-price-mrp]')
    const discountEl = modal.querySelector('[data-spectrum-pdp-discount]')
    const variantsEl = modal.querySelector('[data-spectrum-pdp-variants]')
    const qtyInput = modal.querySelector('[data-spectrum-pdp-qty-input]')
    const qtyDec = modal.querySelector('[data-spectrum-pdp-qty-decrement]')
    const qtyInc = modal.querySelector('[data-spectrum-pdp-qty-increment]')
    const descEl = modal.querySelector('[data-spectrum-pdp-description]')
    const descBody = modal.querySelector('[data-spectrum-pdp-description-body]')
    const descToggle = modal.querySelector('[data-spectrum-pdp-description-toggle]')
    const atcBtn = modal.querySelector('[data-spectrum-pdp-atc]')
    const buyNowBtn = modal.querySelector('[data-spectrum-pdp-buy-now]')
    const viewProductLink = modal.querySelector('[data-spectrum-pdp-view-product]')

    const atcDefaultLabel = atcBtn?.textContent ?? ''
    const buyNowDefaultLabel = buyNowBtn?.textContent ?? ''
    const soldOutLabel =
      typeof cfg.soldOutLabel === 'string' && cfg.soldOutLabel ? cfg.soldOutLabel : 'Sold out'

    let returnFocusTo = null
    let activeProduct = null
    let selectedVariantId = null
    let autoPlayTimer = null

    function stopAutoPlay() {
      if (autoPlayTimer) {
        clearInterval(autoPlayTimer)
        autoPlayTimer = null
      }
    }

    function close() {
      modal.hidden = true
      document.documentElement.classList.remove(`sai-${SNIPPET_ID}-body-locked`)
      stopAutoPlay()
      if (returnFocusTo && typeof returnFocusTo.focus === 'function') {
        try {
          returnFocusTo.focus()
        } catch {}
      }
      returnFocusTo = null
      activeProduct = null
      selectedVariantId = null
    }

    function renderGallery(images) {
      while (trackEl.firstChild) trackEl.removeChild(trackEl.firstChild)
      const count = images.length
      for (const [i, src] of images.entries()) {
        const slide = document.createElement('div')
        slide.className = `sai-${SNIPPET_ID}__pdp-slide`
        slide.setAttribute('role', 'group')
        slide.setAttribute('aria-roledescription', 'slide')
        slide.setAttribute('aria-label', `Image ${i + 1} of ${count}`)
        const img = document.createElement('img')
        img.src = src
        img.alt = ''
        img.loading = i === 0 ? 'eager' : 'lazy'
        slide.appendChild(img)
        trackEl.appendChild(slide)
      }

      const showCarouselNav = cfg.galleryStyle === 'carousel' && count > 1
      if (chevronPrev) chevronPrev.hidden = !(showCarouselNav && cfg.showChevrons)
      if (chevronNext) chevronNext.hidden = !(showCarouselNav && cfg.showChevrons)

      if (dotsEl) {
        while (dotsEl.firstChild) dotsEl.removeChild(dotsEl.firstChild)
        if (showCarouselNav && cfg.showDots) {
          dotsEl.hidden = false
          for (let i = 0; i < count; i++) {
            const dot = document.createElement('button')
            dot.type = 'button'
            dot.className = `sai-${SNIPPET_ID}__pdp-dot${i === 0 ? ' is-active' : ''}`
            dot.setAttribute('aria-label', `Show image ${i + 1}`)
            if (i === 0) dot.setAttribute('aria-selected', 'true')
            dot.addEventListener('click', () => {
              const stride = trackEl.clientWidth
              trackEl.scrollTo({ left: i * stride, behavior: 'smooth' })
            })
            dotsEl.appendChild(dot)
          }
        } else {
          dotsEl.hidden = true
        }
      }
    }

    function updateGalleryActiveState() {
      const stride = trackEl.clientWidth
      if (stride <= 0) return
      const idx = Math.max(
        0,
        Math.min(Math.round(trackEl.scrollLeft / stride), trackEl.children.length - 1),
      )
      if (dotsEl && !dotsEl.hidden) {
        const dots = dotsEl.children
        for (let i = 0; i < dots.length; i++) {
          const active = i === idx
          dots[i].classList.toggle('is-active', active)
          if (active) dots[i].setAttribute('aria-selected', 'true')
          else dots[i].removeAttribute('aria-selected')
        }
      }
      if (chevronPrev && !chevronPrev.hidden) chevronPrev.disabled = idx === 0
      if (chevronNext && !chevronNext.hidden)
        chevronNext.disabled = idx === trackEl.children.length - 1
    }

    function renderVariants(variants) {
      if (!variantsEl) return
      while (variantsEl.firstChild) variantsEl.removeChild(variantsEl.firstChild)

      if (cfg.variantSelectorStyle === 'dropdown') {
        const select = document.createElement('select')
        select.setAttribute('aria-label', 'Choose a variant')
        for (const v of variants) {
          const opt = document.createElement('option')
          opt.value = String(v.id)
          opt.textContent = cfg.showPriceInVariant && v.price ? `${v.title} — ${v.price}` : v.title
          if (v.available === false) opt.disabled = true
          select.appendChild(opt)
        }
        select.addEventListener('change', () => setSelectedVariant(select.value))
        variantsEl.appendChild(select)
        return
      }

      for (const v of variants) {
        const btn = document.createElement('button')
        btn.type = 'button'
        btn.className = `sai-${SNIPPET_ID}__pdp-variant`
        btn.dataset.variantId = String(v.id)
        btn.setAttribute('role', 'radio')
        btn.setAttribute('aria-checked', 'false')

        const titleSpan = document.createElement('span')
        titleSpan.className = `sai-${SNIPPET_ID}__pdp-variant-title`
        titleSpan.textContent = typeof v.title === 'string' ? v.title : ''
        btn.appendChild(titleSpan)

        if (cfg.showPriceInVariant && v.price) {
          const priceSpan = document.createElement('span')
          priceSpan.className = `sai-${SNIPPET_ID}__pdp-variant-price`
          priceSpan.innerHTML = v.price
          btn.appendChild(priceSpan)
        }

        if (v.available === false) {
          btn.disabled = true
          btn.setAttribute('aria-disabled', 'true')
        }

        btn.addEventListener('click', () => {
          if (btn.disabled) return
          setSelectedVariant(v.id)
        })

        variantsEl.appendChild(btn)
      }
    }

    function variantById(id) {
      if (!activeProduct) return null
      const idStr = String(id)
      return activeProduct.variants.find((v) => String(v.id) === idStr) ?? null
    }

    function setSelectedVariant(id) {
      selectedVariantId = id == null ? null : String(id)
      const v = variantById(selectedVariantId)

      if (variantsEl) {
        for (const btn of variantsEl.querySelectorAll(`.sai-${SNIPPET_ID}__pdp-variant`)) {
          const isMatch = btn.dataset.variantId === selectedVariantId
          btn.classList.toggle('is-selected', isMatch)
          btn.setAttribute('aria-checked', isMatch ? 'true' : 'false')
        }
      }

      if (v) {
        if (priceCurrentEl) priceCurrentEl.innerHTML = v.price ?? ''
        if (priceMrpEl) {
          if (v.compareAtPrice && discountPercent(v.price, v.compareAtPrice) !== null) {
            priceMrpEl.innerHTML = v.compareAtPrice
            priceMrpEl.hidden = false
          } else {
            priceMrpEl.hidden = true
            priceMrpEl.innerHTML = ''
          }
        }
        if (discountEl) {
          const text = formatDiscount(v.price, v.compareAtPrice, cfg.discountFormat)
          if (text) {
            discountEl.textContent = text
            discountEl.hidden = false
          } else {
            discountEl.hidden = true
            discountEl.textContent = ''
          }
        }
      }

      const enabled = v != null && v.available !== false
      if (atcBtn) {
        atcBtn.disabled = !enabled
        atcBtn.textContent = enabled ? atcDefaultLabel : soldOutLabel
      }
      if (buyNowBtn) {
        buyNowBtn.disabled = !enabled
        buyNowBtn.textContent = enabled ? buyNowDefaultLabel : soldOutLabel
      }
    }

    function renderDescription(html) {
      if (!descEl || !descBody) return
      descBody.innerHTML = html ?? ''
      if (cfg.descriptionStyle !== 'expandable' || !descToggle) return
      descEl.classList.add('is-collapsed')
      requestAnimationFrame(() => {
        const overflowing = descBody.scrollHeight > descBody.clientHeight + 4
        descToggle.hidden = !overflowing
        descToggle.textContent = 'Read more'
        descToggle.setAttribute('aria-expanded', 'false')
      })
    }

    function getQuantity() {
      if (!qtyInput) return 1
      const n = Number.parseInt(qtyInput.value, 10)
      return Number.isFinite(n) && n > 0 ? n : 1
    }

    function startAutoPlay(slideCount) {
      stopAutoPlay()
      autoPlayTimer = setInterval(() => {
        const stride = trackEl.clientWidth
        if (stride <= 0) return
        const idx = Math.round(trackEl.scrollLeft / stride)
        const next = (idx + 1) % slideCount
        trackEl.scrollTo({ left: next * stride, behavior: 'smooth' })
      }, 4000)
    }

    function open(payload, triggerEl) {
      if (!payload || !Array.isArray(payload.variants) || payload.variants.length === 0) return
      activeProduct = payload
      returnFocusTo = triggerEl ?? null

      if (atcBtn) atcBtn.removeAttribute('aria-busy')
      if (buyNowBtn) buyNowBtn.removeAttribute('aria-busy')

      if (titleEl) titleEl.textContent = payload.title ?? ''
      if (viewProductLink && payload.url) viewProductLink.setAttribute('href', payload.url)

      const images =
        Array.isArray(payload.images) && payload.images.length > 0 ? payload.images : []
      renderGallery(images)
      requestAnimationFrame(() => {
        trackEl.scrollLeft = 0
        updateGalleryActiveState()
      })

      renderVariants(payload.variants)
      const firstAvailable = payload.variants.find((v) => v.available !== false)
      setSelectedVariant(firstAvailable?.id ?? payload.variants[0].id)

      if (qtyInput) qtyInput.value = '1'

      if (cfg.showDescription) {
        renderDescription(payload.description)
      }

      modal.hidden = false
      document.documentElement.classList.add(`sai-${SNIPPET_ID}-body-locked`)

      if (cfg.galleryStyle === 'carousel' && cfg.autoPlay && images.length > 1) {
        startAutoPlay(images.length)
      }

      const focusable =
        modal.querySelector(`.sai-${SNIPPET_ID}__pdp-variant:not(:disabled)`) ?? closeBtn
      focusable?.focus?.()
    }

    backdrop?.addEventListener('click', close)
    closeBtn?.addEventListener('click', close)
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !modal.hidden) close()
    })

    if (chevronPrev) {
      chevronPrev.addEventListener('click', () => {
        const stride = trackEl.clientWidth
        trackEl.scrollBy({ left: -stride, behavior: 'smooth' })
      })
    }
    if (chevronNext) {
      chevronNext.addEventListener('click', () => {
        const stride = trackEl.clientWidth
        trackEl.scrollBy({ left: stride, behavior: 'smooth' })
      })
    }
    trackEl.addEventListener('scroll', updateGalleryActiveState, { passive: true })
    window.addEventListener('resize', updateGalleryActiveState)

    modal.addEventListener('mouseenter', stopAutoPlay)
    modal.addEventListener('focusin', stopAutoPlay)

    if (qtyDec && qtyInput) {
      qtyDec.addEventListener('click', () => {
        const n = getQuantity()
        qtyInput.value = String(Math.max(1, n - 1))
      })
    }
    if (qtyInc && qtyInput) {
      qtyInc.addEventListener('click', () => {
        qtyInput.value = String(getQuantity() + 1)
      })
    }

    if (descToggle && descEl) {
      descToggle.addEventListener('click', () => {
        const collapsed = descEl.classList.toggle('is-collapsed')
        descToggle.textContent = collapsed ? 'Read more' : 'Read less'
        descToggle.setAttribute('aria-expanded', collapsed ? 'false' : 'true')
      })
    }

    if (atcBtn) {
      atcBtn.addEventListener('click', async () => {
        if (selectedVariantId == null || atcBtn.disabled) return
        atcBtn.disabled = true
        atcBtn.setAttribute('aria-busy', 'true')
        const startedAt = Date.now()
        const quantity = getQuantity()
        try {
          const result = await addVariantToCart(selectedVariantId, quantity)
          const elapsed = Date.now() - startedAt
          if (elapsed < MIN_SPINNER_MS) {
            await new Promise((r) => setTimeout(r, MIN_SPINNER_MS - elapsed))
          }
          notifyCartUpdate({
            variantId: selectedVariantId,
            productId: activeProduct?.productId,
            response: result,
          })
          trackFn(`${FEATURE_SLUG}:add_to_cart`, {
            product_id: activeProduct?.productId ?? null,
            product_handle: productHandleFromUrl(activeProduct?.url),
            variant_id: selectedVariantId,
            quantity,
            source: 'mini_pdp',
          })
          close()
        } catch (err) {
          console.warn('[Search PLP] Add to cart failed', err)
          atcBtn.disabled = false
          atcBtn.removeAttribute('aria-busy')
        }
      })
    }

    if (buyNowBtn) {
      buyNowBtn.addEventListener('click', async () => {
        if (selectedVariantId == null || buyNowBtn.disabled) return
        buyNowBtn.disabled = true
        buyNowBtn.setAttribute('aria-busy', 'true')
        const startedAt = Date.now()
        try {
          await addVariantToCart(selectedVariantId, getQuantity())
          const elapsed = Date.now() - startedAt
          if (elapsed < MIN_SPINNER_MS) {
            await new Promise((r) => setTimeout(r, MIN_SPINNER_MS - elapsed))
          }
          window.location.href = '/checkout'
        } catch (err) {
          console.warn('[Search PLP] Buy now failed', err)
          buyNowBtn.disabled = false
          buyNowBtn.removeAttribute('aria-busy')
        }
      })
    }

    return { open, close }
  }

  // ── DOM string builders ──

  function placeholderSvg() {
    return '<svg class="sai-srpfrd01__product-placeholder" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-5-5L5 21"/></svg>'
  }

  function buildProductCard(product, settings) {
    const img = product.featured_image || product.images?.[0] || ''
    const imgSrc = typeof img === 'string' ? img : img.src || ''
    const title = product.title || ''
    const vendor = product.vendor || ''
    // Price and MRP must come from ONE variant. The product-level fields are independent
    // aggregates — `price` is the CHEAPEST variant's price while `compare_at_price` is the
    // DEAREST variant's compare-at — so pairing them advertises a discount no variant was ever
    // sold at. The Vajra chair showed its ₹49,000 model against the ₹1,00,000 MRP belonging to
    // its ₹69,000 model: 51% off instead of that model's real 35%.
    //
    // Do NOT put `selected_or_first_available_variant` first here: the search API re-points it
    // to the variant that MATCHED the query, while this card links to the bare PDP, which opens
    // on the product's DEFAULT variant. Quoting the matched one makes the card and the page it
    // opens disagree ("₹49,000" on the card, ₹69,000 on arrival). Take the first available
    // variant in Shopify's own order, which is what the PDP shows. If the card ever starts
    // linking with ?variant=, prefer the matched variant again and the mismatch inverts.
    //
    // Only variants that actually carry a price are eligible, so the pair can never be
    // half-resolved into a variant price with a product-level compare-at.
    const hasPrice = (v) => v && v.price != null
    const cardVariant =
      [
        (product.variants || []).find((v) => v.available !== false),
        (product.variants || [])[0],
        product.selected_or_first_available_variant,
      ].find(hasPrice) || null
    const priceValue = cardVariant ? cardVariant.price : product.price
    // Read the compare-at from the SAME source as the price, never a mix of the two.
    const compareValue = cardVariant ? cardVariant.compare_at_price : product.compare_at_price
    const price = formatMoney(priceValue)
    const compareAt = compareValue && compareValue > priceValue ? formatMoney(compareValue) : ''

    const hoverShadow = settings.cardHoverShadow
    const hoverSwap = settings.cardHoverSwapImage
    const showCta = settings.showCta
    const enableMiniPdp = settings.enableMiniPdp
    const ctaLabel = settings.ctaLabel
    const soldOutLabel = settings.soldOutLabel
    const enableNavigateToPdp = settings.enableNavigateToPdp
    const showCurrentPrice = settings.showCurrentPrice
    const showMrp = settings.showMrp
    const showDiscountBadge = settings.showDiscountBadge
    const discountFmt = settings.discountFormat

    // Single product image — no hover image-swap (matches the collection card).
    let imgHtml = ''
    if (imgSrc) {
      imgHtml = `<div class="sai-srpfrd01__product-img-wrap"><img class="sai-srpfrd01__product-img" src="${escAttr(imgSrc)}" alt="${escAttr(title)}" loading="lazy"></div>`
    } else {
      imgHtml = `<div class="sai-srpfrd01__product-img-wrap">${placeholderSvg()}</div>`
    }

    const vendorHtml = vendor
      ? `<div class="sai-srpfrd01__product-vendor">${escAttr(vendor)}</div>`
      : ''

    // Build price row. Order is selling price → strikethrough MRP → discount,
    // matching the storefront's card layout ("₹3,999 ₹5,000 20% OFF").
    let priceHtml = '<div class="sai-srpfrd01__product-price-row">'
    if (showCurrentPrice && price) {
      priceHtml += `<span class="sai-srpfrd01__product-price">${price}</span>`
    }
    if (showMrp && compareAt) {
      priceHtml += `<span class="sai-srpfrd01__product-compare">${compareAt}</span>`
    }
    if (showDiscountBadge && compareAt) {
      const discountText = formatDiscount(price, compareAt, discountFmt)
      if (discountText) {
        priceHtml += `<span class="sai-srpfrd01__product-discount">${discountText}</span>`
      }
    }
    priceHtml += '</div>'

    const variants = product.variants || []
    const isSingleVariant = variants.length === 1
    const firstVariant = variants[0]
    const isAvailable = isSingleVariant && firstVariant && firstVariant.available
    const isSoldOut = isSingleVariant && firstVariant && !firstVariant.available

    // CTA delegates to the shared theme quick-add (spectrum-quick-add.js, bound
    // globally in theme.liquid) so search cards get the same mini-PDP + ATC as
    // the collection PLP card. Single-variant → direct add; multi-variant →
    // opens the shared #spectrum-quick-add-dialog. No per-card JS handler.
    let ctaHtml = ''
    if (showCta) {
      if (isSoldOut) {
        ctaHtml = `<button class="sai-srpfrd01__card-atc" type="button" disabled>${escAttr(soldOutLabel)}</button>`
      } else if (isAvailable) {
        ctaHtml = `<button class="sai-srpfrd01__card-atc" type="button" data-spectrum-direct-atc data-variant-id="${firstVariant.id}">${escAttr(ctaLabel)}</button>`
      } else {
        ctaHtml = `<button class="sai-srpfrd01__card-quickview" type="button" data-spectrum-quick-add-trigger="${escAttr(product.handle || '')}" data-product-id="${product.id || ''}" data-default-variant-id="${firstVariant ? firstVariant.id : ''}">${escAttr(ctaLabel)}</button>`
      }
    }

    return `<div class="sai-srpfrd01__product" tabindex="0" data-handle="${escAttr(product.handle || '')}" data-product-id="${product.id || ''}" data-hover-shadow="${hoverShadow}" data-navigate-to-pdp="${enableNavigateToPdp}">${imgHtml}<div class="sai-srpfrd01__product-info">${vendorHtml}<div class="sai-srpfrd01__product-title">${escAttr(title)}</div>${priceHtml}</div>${ctaHtml}</div>`
  }

  function buildSkeletonCard() {
    return (
      '<div class="sai-srpfrd01__skeleton">' +
      '<div class="sai-srpfrd01__skeleton-img">​</div>' +
      '<div class="sai-srpfrd01__skeleton-line sai-srpfrd01__skeleton-line--title">​</div>' +
      '<div class="sai-srpfrd01__skeleton-line sai-srpfrd01__skeleton-line--price">​</div>' +
      '</div>'
    )
  }

  function buildSkeletonGrid(count) {
    return Array.from({ length: count }, buildSkeletonCard).join('')
  }

  // ── Custom Element ──

  if (!customElements.get(TAG)) {
    class SaiSearchPage extends HTMLElement {
      connectedCallback() {
        if (this._initialized) return
        this._initialized = true

        this._track = noopTrack
        this._searchSeq = 0
        this._currentPage = 1
        this._totalPages = 1
        this._totalHits = 0
        this._currentSort = ''
        this._activeFilters = {}
        this._pendingFilters = {}
        this._products = []
        this._facets = null
        this._appliedFilters = null
        this._sortOptions = null
        this._sortOptionsKey = null
        this._activeMobileGroup = null
        this._infiniteObserver = null
        this._miniPdp = null

        this._readSettings()
        this._cacheEls()
        this._readUrlState()
        this._bindEvents()
        this._applyResponsive()

        // Initialize mini-PDP from the closest wrapper container. The
        // snippet container (with data-spectrum-instance-id) is where
        // initMiniPdp finds the data-spectrum-pdp-modal element.
        const container = this.closest('[data-spectrum-instance-id]') ?? this.parentElement
        if (container) {
          this._miniPdp = initMiniPdp(container, (...args) => this._track(...args))
        }

        this._doSearch()
      }

      setAnalytics(track) {
        this._track = typeof track === 'function' ? track : noopTrack
        // Re-init mini-PDP with the updated track function so analytics
        // from the modal (ATC, Buy Now) use the SDK-provided tracker.
        const container = this.closest('[data-spectrum-instance-id]') ?? this.parentElement
        if (container) {
          this._miniPdp = initMiniPdp(container, (...args) => this._track(...args))
        }
      }

      disconnectedCallback() {
        if (this._keydownHandler) document.removeEventListener('keydown', this._keydownHandler)
        if (this._popstateHandler) window.removeEventListener('popstate', this._popstateHandler)
        if (this._scrollHandler) window.removeEventListener('scroll', this._scrollHandler)
        if (this._infiniteObserver) this._infiniteObserver.disconnect()
      }

      _isMobile() {
        return window.matchMedia('(max-width: 990px)').matches
      }

      _activePaginationStyle() {
        return this._isMobile() ? this._paginationStyleMobile : this._paginationStyleDesktop
      }

      _applyGridColumns() {
        const cols = this._isMobile() ? this._gridColumnsMobile : this._gridColumnsDesktop
        this.style.setProperty('--sai-srpfrd01-cols', String(cols))
      }

      _readSettings() {
        this._resultsPerPage = Number(this.getAttribute('data-results-per-page')) || 10
        this._paginationStyleDesktop =
          this.getAttribute('data-pagination-style-desktop') || 'numbered'
        this._paginationStyleMobile =
          this.getAttribute('data-pagination-style-mobile') || 'infinite_scroll'
        this._showFacetFilters = this.getAttribute('data-show-facet-filters') !== 'false'
        this._showSort = this.getAttribute('data-show-sort') !== 'false'
        this._gridColumnsDesktop = Number(this.getAttribute('data-grid-columns-desktop')) || 4
        this._gridColumnsMobile = Number(this.getAttribute('data-grid-columns-mobile')) || 2

        // Card settings
        const ar = this.getAttribute('data-image-aspect-ratio') || '1/1'
        this.style.setProperty('--sai-srpfrd01-img-ratio', ar)
        const fit = this.getAttribute('data-image-object-fit') || 'cover'
        this.style.setProperty('--sai-srpfrd01-img-fit', fit)

        this._cardSettings = {
          cardHoverShadow: this.getAttribute('data-card-hover-shadow') !== 'false',
          cardHoverSwapImage: this.getAttribute('data-card-hover-swap-image') === 'true',
          showCta: this.getAttribute('data-show-cta') !== 'false',
          enableMiniPdp: this.getAttribute('data-enable-mini-pdp') !== 'false',
          ctaLabel: this.getAttribute('data-cta-label') || 'Add To Cart',
          soldOutLabel: this.getAttribute('data-sold-out-label') || 'Sold out',
          enableNavigateToPdp: this.getAttribute('data-enable-navigate-to-pdp') !== 'false',
          showCurrentPrice: this.getAttribute('data-show-current-price') !== 'false',
          showMrp: this.getAttribute('data-show-mrp') !== 'false',
          showDiscountBadge: this.getAttribute('data-show-discount-badge') !== 'false',
          discountFormat: this.getAttribute('data-discount-format') || 'percent',
        }

        // No-results featured collection fallback
        this._showFeatured = this.getAttribute('data-show-featured') !== 'false'
        this._featuredTitle = this.getAttribute('data-featured-title') || 'Featured Products'
        this._featuredHandle = this.getAttribute('data-featured-handle') || 'all'
        this._featuredMaxItems = Number(this.getAttribute('data-featured-max-items')) || 8
        this._featuredViewAllUrl = this.getAttribute('data-featured-view-all-url') || ''
        this._featuredLayout = this.getAttribute('data-featured-layout') || 'grid'
        this._featuredCache = null
        this._featuredPromise = null

        this._applyGridColumns()
      }

      _cacheEls() {
        this._els = {
          searchInput: this.querySelector('.sai-srpfrd01__search-input'),
          searchClear: this.querySelector('.sai-srpfrd01__search-clear'),
          activeFilters: this.querySelector('.sai-srpfrd01__active-filters'),
          toolbar: this.querySelector('.sai-srpfrd01__toolbar'),
          resultsHeading: this.querySelector('.sai-srpfrd01__results-heading'),
          sortWrap: this.querySelector('.sai-srpfrd01__sort-wrap'),
          sortSelect: this.querySelector('.sai-srpfrd01__sort-select'),
          sidebar: this.querySelector('.sai-srpfrd01__filter-sidebar'),
          content: this.querySelector('.sai-srpfrd01__content'),
          loading: this.querySelector('.sai-srpfrd01__loading'),
          skeletonGrid: this.querySelector('.sai-srpfrd01__skeleton-grid'),
          grid: this.querySelector('.sai-srpfrd01__grid'),
          noResults: this.querySelector('.sai-srpfrd01__no-results'),
          featured: this.querySelector('.sai-srpfrd01__featured'),
          featuredTitle: this.querySelector('.sai-srpfrd01__featured-title'),
          featuredGrid: this.querySelector('.sai-srpfrd01__featured-grid'),
          featuredViewAll: this.querySelector('.sai-srpfrd01__featured-view-all'),
          pagination: this.querySelector('.sai-srpfrd01__pagination'),
          mobileFooter: this.querySelector('.sai-srpfrd01__mobile-footer'),
          mobileFilterBtn: this.querySelector('.sai-srpfrd01__mobile-filter-btn'),
          mobileSortBtn: this.querySelector('.sai-srpfrd01__mobile-sort-btn'),
          filterOverlay: this.querySelector('.sai-srpfrd01__filter-overlay'),
          filterOverlayClose: this.querySelector('.sai-srpfrd01__filter-overlay-close'),
          filterGroups: this.querySelector('.sai-srpfrd01__filter-groups'),
          filterValues: this.querySelector('.sai-srpfrd01__filter-values'),
          filterReset: this.querySelector('.sai-srpfrd01__filter-reset'),
          filterApply: this.querySelector('.sai-srpfrd01__filter-apply'),
          sortSheet: this.querySelector('.sai-srpfrd01__sort-sheet'),
          sortSheetBackdrop: this.querySelector('.sai-srpfrd01__sort-sheet-backdrop'),
          sortSheetPanel: this.querySelector('.sai-srpfrd01__sort-sheet-panel'),
          sortSheetClose: this.querySelector('.sai-srpfrd01__sort-sheet-close'),
          sortSheetOptions: this.querySelector('.sai-srpfrd01__sort-sheet-options'),
        }
      }

      // ── URL State ──

      _readUrlState() {
        const params = new URLSearchParams(window.location.search)
        const q = params.get('q') || ''
        this._els.searchInput.value = q
        this._els.searchClear.hidden = !q
        this._currentSort = params.get('sort') || ''
        this._currentPage = Number(params.get('page')) || 1

        if (this._els.sortSelect) this._els.sortSelect.value = this._currentSort

        this._activeFilters = {}
        for (const entry of params.entries()) {
          if (entry[0].startsWith('filter.')) {
            this._activeFilters[entry[0]] = entry[1]
          }
        }
      }

      _writeUrlState(opts) {
        const params = new URLSearchParams()
        const q = (this._els.searchInput.value || '').trim()
        if (q) params.set('q', q)
        if (this._currentSort) params.set('sort', this._currentSort)
        if (this._currentPage > 1) params.set('page', String(this._currentPage))

        const keys = Object.keys(this._activeFilters).sort()
        for (let i = 0; i < keys.length; i++) {
          params.set(keys[i], this._activeFilters[keys[i]])
        }

        const newUrl = window.location.pathname + (params.toString() ? `?${params.toString()}` : '')
        if (opts?.replace) {
          history.replaceState(null, '', newUrl)
        } else {
          history.pushState(null, '', newUrl)
        }
      }

      // ── Events ──

      _bindEvents() {
        const els = this._els

        els.searchInput.addEventListener('input', () => {
          els.searchClear.hidden = !els.searchInput.value
        })

        els.searchInput.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            this._currentPage = 1
            this._writeUrlState()
            this._doSearch()
          }
        })

        els.searchClear.addEventListener('click', () => {
          els.searchInput.value = ''
          els.searchClear.hidden = true
          this._currentPage = 1
          this._activeFilters = {}
          this._currentSort = ''
          if (els.sortSelect) els.sortSelect.value = ''
          this._writeUrlState()
          this._doSearch()
          els.searchInput.focus()
        })

        if (els.sortSelect && this._showSort) {
          els.sortSelect.addEventListener('change', () => {
            this._currentSort = els.sortSelect.value
            this._currentPage = 1
            this._writeUrlState()
            this._doSearch()
            this._track(`${FEATURE_SLUG}:sort_elem_clicked`, { sort: this._currentSort })
          })
        }

        // Mobile footer buttons
        if (els.mobileFilterBtn) {
          els.mobileFilterBtn.addEventListener('click', () => {
            this._openFilterOverlay()
          })
        }
        if (els.mobileSortBtn) {
          els.mobileSortBtn.addEventListener('click', () => {
            this._openSortSheet()
          })
        }

        // Filter overlay controls
        if (els.filterOverlayClose) {
          els.filterOverlayClose.addEventListener('click', () => {
            this._closeFilterOverlay()
          })
        }
        if (els.filterReset) {
          els.filterReset.addEventListener('click', () => {
            this._pendingFilters = {}
            this._renderMobileFilterValues()
          })
        }
        if (els.filterApply) {
          els.filterApply.addEventListener('click', () => {
            this._activeFilters = Object.assign({}, this._pendingFilters)
            this._currentPage = 1
            this._closeFilterOverlay()
            this._writeUrlState()
            this._doSearch()
          })
        }

        // Sort sheet controls
        if (els.sortSheetBackdrop) {
          els.sortSheetBackdrop.addEventListener('click', () => {
            this._closeSortSheet()
          })
        }
        if (els.sortSheetClose) {
          els.sortSheetClose.addEventListener('click', () => {
            this._closeSortSheet()
          })
        }

        // Keyboard — close overlays
        this._keydownHandler = (e) => {
          if (e.key === 'Escape') {
            if (els.filterOverlay && !els.filterOverlay.hidden) {
              this._closeFilterOverlay()
              return
            }
            if (els.sortSheet && !els.sortSheet.hidden) {
              this._closeSortSheet()
              return
            }
          }
        }
        document.addEventListener('keydown', this._keydownHandler)

        // History navigation
        this._popstateHandler = () => {
          this._readUrlState()
          this._doSearch()
        }
        window.addEventListener('popstate', this._popstateHandler)
      }

      _applyResponsive() {
        const els = this._els
        if (this._showSort) {
          els.sortWrap.hidden = false
        }
        if (this._showSort) {
          els.mobileFooter.hidden = false
          els.mobileSortBtn.hidden = false
        }
        if (!this._showSort) els.mobileSortBtn.hidden = true
        els.mobileFilterBtn.hidden = true
      }

      // ── Search ──

      async _doSearch() {
        const els = this._els
        let api = searchApi()

        // SDK may load after this snippet — wait up to 3s
        if (!api) {
          for (let _w = 0; _w < 15 && !api; _w++) {
            await new Promise((r) => {
              setTimeout(r, 200)
            })
            api = searchApi()
          }
        }

        const q = (els.searchInput.value || '').trim()

        if (!api) {
          this._hideAll()
          els.noResults.textContent = 'Search is not available. Please try again later.'
          els.noResults.hidden = false
          return
        }

        const searchId = ++this._searchSeq
        const paginationStyle = this._activePaginationStyle()
        const isAppend = paginationStyle === 'infinite_scroll' && this._currentPage > 1

        if (!isAppend) {
          this._hideAll()
          // Drop stale labeled chips; _loadFacets repopulates them.
          this._appliedFilters = null
          els.skeletonGrid.innerHTML = buildSkeletonGrid(this._resultsPerPage)
          els.loading.hidden = false
        }

        const opts = {
          hitsPerPage: this._resultsPerPage,
          page: this._currentPage,
        }
        if (this._currentSort) opts.sort = this._currentSort
        if (Object.keys(this._activeFilters).length) opts.filters = this._activeFilters

        const searchPromise = api.query(q, opts)

        if (!isAppend) {
          if (this._showFacetFilters) {
            this._showFacetShimmer()
            this._showMobileFooterShimmer()
            this._loadFacets(q)
          } else {
            this._hideFacets()
          }
        }

        const result = await searchPromise

        if (searchId !== this._searchSeq) return

        els.loading.hidden = true

        if (!result || !result.ok || !result.data) {
          els.grid.hidden = true
          els.noResults.textContent = 'Something went wrong. Try again.'
          els.noResults.hidden = false
          return
        }

        const data = result.data
        const products = data.products || []
        this._totalPages = data.totalPages || 1
        this._totalHits = data.totalHits || 0

        // Header: "Showing <N> Products for “<query>”". The query is set via
        // textContent (never innerHTML) so a hostile search term can't inject
        // markup; it gets its own span so the design can size it larger than
        // the surrounding copy. A bare /search (no query) drops the "for …".
        els.resultsHeading.replaceChildren(
          document.createTextNode(`Showing ${this._totalHits} Products`),
        )
        if (q) {
          els.resultsHeading.append(document.createTextNode(' for '))
          const _qSpan = document.createElement('span')
          _qSpan.className = 'sai-srpfrd01__results-query'
          _qSpan.textContent = `“${q}”`
          els.resultsHeading.append(_qSpan)
        }

        if (products.length === 0) {
          els.grid.hidden = true
          els.noResults.textContent = `No results found${q ? ` for "${q}"` : ''}.`
          els.noResults.hidden = false
          els.pagination.hidden = true
          this._renderActiveFilters()
          this._renderFeatured()
          this._track(`${FEATURE_SLUG}:no_results`, { query: q })
          return
        }

        const settings = this._cardSettings
        if (isAppend) {
          this._products = this._products.concat(products)
          els.grid.insertAdjacentHTML(
            'beforeend',
            products.map((p) => buildProductCard(p, settings)).join(''),
          )
        } else {
          this._products = products
          els.grid.innerHTML = products.map((p) => buildProductCard(p, settings)).join('')
        }

        els.grid.hidden = false
        els.noResults.hidden = true
        if (els.featured) els.featured.hidden = true
        this._renderPagination()
        this._renderActiveFilters()
        this._bindProductCards()

        if (api && q) api.addRecent(q)

        this._track(`${FEATURE_SLUG}:results`, {
          query: q,
          result_count: products.length,
          total_hits: this._totalHits,
          page: this._currentPage,
          sort: this._currentSort,
        })
      }

      _hideAll() {
        const els = this._els
        els.loading.hidden = true
        els.grid.hidden = true
        els.noResults.hidden = true
        els.pagination.hidden = true
        if (els.featured) els.featured.hidden = true
      }

      // Fetch the featured-collection products (Shopify AJAX). Prices come back
      // as currency-major strings, so callers must convert to cents.
      async _fetchFeatured() {
        if (this._featuredCache) return this._featuredCache
        if (this._featuredPromise) return this._featuredPromise
        this._featuredPromise = (async () => {
          try {
            const res = await fetch(
              `/collections/${encodeURIComponent(this._featuredHandle)}/products.json?limit=${this._featuredMaxItems}`,
            )
            if (!res.ok) return null
            const json = await res.json()
            this._featuredCache = json.products || []
            return this._featuredCache
          } catch {
            return null
          } finally {
            this._featuredPromise = null
          }
        })()
        return this._featuredPromise
      }

      // Render the featured collection under the no-results message. Reuses the
      // PLP product card so the fallback grid matches the search results.
      async _renderFeatured() {
        const els = this._els
        if (!this._showFeatured || !els.featured) return
        const products = await this._fetchFeatured()
        // Bail if a real search resolved while we were fetching.
        if (!els.grid.hidden) return
        if (!products || products.length === 0) {
          els.featured.hidden = true
          return
        }

        // /collections/*.json prices are currency-major strings ("800.00"); normalise to the
        // cents that buildProductCard + formatMoney expect.
        //
        // The VARIANTS must be converted too, not just the product-level fields. buildProductCard
        // resolves a variant and reads price/compare_at off it (so the two can never come from
        // different variants). Spreading `...p` with untouched variants left it reading the raw
        // "800.00" string, which formatMoney then divided by 100 — an ₹800 eye mask rendered as
        // ₹8, a ₹17,500 chair as ₹175. Product-level normalisation alone is invisible here
        // because the card no longer reads those fields on the happy path.
        const cents = (v) => (v == null || v === '' ? null : Math.round(Number(v) * 100))
        const normalized = products.map((p) => {
          const variants = (p.variants || []).map((v) => ({
            ...v,
            price: cents(v.price) ?? 0,
            compare_at_price: cents(v.compare_at_price),
          }))
          const v0 = variants[0] || {}
          const compareAt = v0.compare_at_price
          return {
            ...p,
            variants,
            price: v0.price ?? 0,
            compare_at_price: compareAt && compareAt > (v0.price ?? 0) ? compareAt : null,
          }
        })

        els.featuredTitle.textContent = this._featuredTitle
        els.featuredTitle.hidden = !this._featuredTitle
        els.featuredGrid.className =
          this._featuredLayout === 'scroll'
            ? 'sai-srpfrd01__featured-grid sai-srpfrd01__featured-grid--scroll'
            : 'sai-srpfrd01__featured-grid'
        els.featuredGrid.innerHTML = normalized
          .map((p) => buildProductCard(p, this._cardSettings))
          .join('')
        this._bindProductCards(els.featuredGrid)

        if (els.featuredViewAll) {
          const href = this._featuredViewAllUrl || `/collections/${this._featuredHandle}`
          els.featuredViewAll.href = href
          els.featuredViewAll.textContent = 'View all'
          els.featuredViewAll.hidden = false
        }
        els.featured.hidden = false
      }

      _showFacetShimmer() {
        const els = this._els
        let shimmer = ''
        for (let s = 0; s < 4; s++) {
          shimmer += '<div class="sai-srpfrd01__facet-shimmer-group">'
          shimmer +=
            '<div class="sai-srpfrd01__skeleton-line" style="inline-size:60%;block-size:16px;margin-block:12px 8px">​</div>'
          for (let si = 0; si < 4; si++) {
            shimmer += `<div class="sai-srpfrd01__skeleton-line" style="inline-size:${50 + Math.random() * 40}%;block-size:12px;margin-block:6px">​</div>`
          }
          shimmer += '</div>'
        }
        els.sidebar.innerHTML = shimmer
        els.sidebar.hidden = false
      }

      // Mobile footer (filter + sort) loading state. The filter and dynamic
      // sort options both come from the async facets fetch, so the mobile
      // footer buttons shimmer in place while it resolves instead of popping in.
      _showMobileFooterShimmer() {
        const els = this._els
        if (!els.mobileFooter) return
        if (!this._showFacetFilters && !this._showSort) return
        els.mobileFooter.hidden = false
        if (els.mobileFilterBtn) {
          els.mobileFilterBtn.hidden = !this._showFacetFilters
          els.mobileFilterBtn.classList.toggle('is-loading', this._showFacetFilters)
          els.mobileFilterBtn.disabled = this._showFacetFilters
        }
        if (els.mobileSortBtn) {
          els.mobileSortBtn.hidden = !this._showSort
          els.mobileSortBtn.classList.toggle('is-loading', this._showSort)
          els.mobileSortBtn.disabled = this._showSort
        }
      }

      _clearMobileFooterShimmer() {
        const els = this._els
        if (els.mobileFilterBtn) {
          els.mobileFilterBtn.classList.remove('is-loading')
          els.mobileFilterBtn.disabled = false
        }
        if (els.mobileSortBtn) {
          els.mobileSortBtn.classList.remove('is-loading')
          els.mobileSortBtn.disabled = false
        }
      }

      _hideFacets() {
        this._els.sidebar.hidden = true
        this._clearMobileFooterShimmer()
        if (this._els.mobileFilterBtn) this._els.mobileFilterBtn.hidden = true
        // The sort button doesn't depend on facets — keep it on mobile when
        // sorting is enabled, and keep the footer visible to host it.
        if (this._els.mobileSortBtn) this._els.mobileSortBtn.hidden = !this._showSort
        if (this._els.mobileFooter) this._els.mobileFooter.hidden = !this._showSort
      }

      async _loadFacets(q) {
        const els = this._els
        const api = searchApi()
        if (!api) {
          this._hideFacets()
          return
        }

        const opts = {}
        if (Object.keys(this._activeFilters).length) opts.filters = this._activeFilters

        let result
        try {
          result = await api.getFacets(q, opts)
        } catch {
          this._hideFacets()
          return
        }
        if (!result || !result.ok || !result.data) {
          this._hideFacets()
          return
        }

        const raw = result.data
        const facetList = Array.isArray(raw?.facets) ? raw.facets : Array.isArray(raw) ? raw : null

        // Capture the labeled applied-filter chips and dynamic sort options the
        // facets endpoint returns alongside the facet list.
        this._appliedFilters = Array.isArray(raw?.appliedFilters) ? raw.appliedFilters : null
        this._applySortFromApi(raw?.sort)

        if (!facetList || !facetList.length) {
          this._renderActiveFilters()
          this._hideFacets()
          return
        }

        this._facets = facetList
          .map(normalizeFacet)
          .filter(Boolean)
          .sort((a, b) => a.position - b.position)
        if (!this._facets.length) {
          this._renderActiveFilters()
          this._hideFacets()
          return
        }
        this._clearMobileFooterShimmer()
        if (els.mobileFilterBtn) els.mobileFilterBtn.hidden = false
        if (els.mobileSortBtn) els.mobileSortBtn.hidden = !this._showSort
        if (els.mobileFooter) els.mobileFooter.hidden = false
        this._renderSidebar()
        // Re-render chips now that labeled appliedFilters are available.
        this._renderActiveFilters()
      }

      // ── Dynamic sort options ──
      // The facets endpoint may return a `sort` block describing the brand's
      // configured sort options. When present, it replaces the SSR'd defaults.

      _applySortFromApi(sort) {
        if (!sort || !Array.isArray(sort.options) || !sort.options.length) return

        const options = sort.options
          .filter((o) => o?.key && o.enabled !== false)
          .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
          .map((o) => ({ value: o.key, label: o.label || o.key }))
        if (!options.length) return

        const optionsKey = options.map((o) => o.value).join('|')
        const valid = new Set(options.map((o) => o.value))

        // Adopt the server's active sort only when the visitor hasn't chosen
        // one (no `sort` in the URL) — never override an explicit selection.
        if (!this._currentSort && sort.activeKey && valid.has(sort.activeKey)) {
          this._currentSort = sort.activeKey
        }

        this._sortOptions = options
        if (this._sortOptionsKey === optionsKey) return
        this._sortOptionsKey = optionsKey

        const sel = this._els.sortSelect
        if (sel) {
          sel.innerHTML = options
            .map((o) => `<option value="${escAttr(o.value)}">${escAttr(o.label)}</option>`)
            .join('')
          if (valid.has(this._currentSort)) sel.value = this._currentSort
        }
      }

      // ── Sidebar (desktop) ──

      _renderSidebar() {
        const els = this._els
        if (!this._showFacetFilters || !this._facets || !this._facets.length) {
          this._hideFacets()
          return
        }
        let html = ''

        for (let i = 0; i < this._facets.length; i++) {
          const facet = this._facets[i]
          const filterParam = fieldToFilterParam(facet.field)

          html += '<details class="sai-srpfrd01__facet-group" open>'
          html += `<summary class="sai-srpfrd01__facet-header">${escAttr(facet.label)}`
          html +=
            '<svg class="sai-srpfrd01__facet-arrow" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>'
          html += '</summary>'
          html += '<div class="sai-srpfrd01__facet-body">'
          html += this._facetBodyHtml(facet, filterParam, this._activeFilters, true)
          html += '</div></details>'
        }

        if (!html) {
          this._hideFacets()
          return
        }

        els.sidebar.innerHTML = html
        els.sidebar.hidden = false
        this._bindFacetControls(els.sidebar, this._activeFilters, true)
      }

      // Build the inner controls for one facet. Shared by the desktop sidebar
      // and the mobile overlay; `filters` is the map being edited
      // (_activeFilters live, _pendingFilters in the mobile overlay) and
      // `live` toggles immediate-apply affordances (per-range Apply button).
      _facetBodyHtml(facet, filterParam, filters, live) {
        if (facet.kind === 'range') {
          const step = facet.unit === 'percent' ? '1' : '0.01'
          const curMin = filters[`${filterParam}.gte`] || ''
          const curMax = filters[`${filterParam}.lte`] || ''
          // Slider domain is the exact facet bounds (not rounded) so the inputs
          // can display the true min/max.
          const sMin = facet.min
          const sMax = facet.max
          // Inputs are pre-filled with the current bound (min/max when unset)
          // rather than left blank with a placeholder. A value sitting at the
          // bound is treated as "no filter" at commit time (see commitRange).
          const gteFill = curMin === '' ? sMin : curMin
          const lteFill = curMax === '' ? sMax : curMax
          const k = escAttr(filterParam)

          let html = '<div class="sai-srpfrd01__range-wrap">'
          // Rail + fill are ::before/::after on the slider (not child divs) —
          // Dawn's `div:empty { display:none }` reset would hide empty divs.
          html += '<div class="sai-srpfrd01__range-slider">'
          html += `<input type="range" class="sai-srpfrd01__range-thumb" data-filter-key="${k}" data-bound="gte" min="${sMin}" max="${sMax}" step="${step}" value="${escAttr(gteFill)}" aria-label="${escAttr(facet.label)} minimum">`
          html += `<input type="range" class="sai-srpfrd01__range-thumb" data-filter-key="${k}" data-bound="lte" min="${sMin}" max="${sMax}" step="${step}" value="${escAttr(lteFill)}" aria-label="${escAttr(facet.label)} maximum">`
          html += '</div>'
          html += '<div class="sai-srpfrd01__facet-range">'
          html += `<label class="sai-srpfrd01__range-field"><input type="number" class="sai-srpfrd01__range-input" data-filter-key="${k}" data-bound="gte" value="${escAttr(gteFill)}" min="${sMin}" max="${sMax}" step="${step}"></label>`
          html += '<span class="sai-srpfrd01__range-sep">to</span>'
          html += `<label class="sai-srpfrd01__range-field"><input type="number" class="sai-srpfrd01__range-input" data-filter-key="${k}" data-bound="lte" value="${escAttr(lteFill)}" min="${sMin}" max="${sMax}" step="${step}"></label>`
          html += '</div></div>'
          return html
        }

        if (facet.control === 'toggle') {
          const trueEntry = facet.values.find((v) => v.value === 'true')
          const value = trueEntry ? 'true' : facet.values[0]?.value || 'true'
          const isOn = valueInFilter(filters, filterParam, value)
          let html = '<label class="sai-srpfrd01__facet-toggle">'
          html += `<input type="checkbox" data-filter-key="${escAttr(filterParam)}" data-filter-value="${escAttr(value)}"${isOn ? ' checked' : ''}>`
          html +=
            '<span class="sai-srpfrd01__toggle-track"><span class="sai-srpfrd01__toggle-thumb"></span></span>'
          html += `<span>${escAttr(facet.label)}</span>`
          html += '</label>'
          return html
        }

        if (facet.control === 'swatch') {
          let html = '<div class="sai-srpfrd01__facet-swatches">'
          for (let j = 0; j < facet.values.length; j++) {
            const v = facet.values[j]
            const isChecked = valueInFilter(filters, filterParam, v.value)
            // Resolved image → thumbnail; hex → colour fill; otherwise a
            // placeholder so a value missing its displayConfig still shows in
            // the grid: an image-frame icon for image fields, a neutral dot for
            // swatch fields.
            let dot
            if (v.imageUrl) {
              dot = `<span class="sai-srpfrd01__swatch-dot sai-srpfrd01__swatch-dot--image"><img class="sai-srpfrd01__swatch-img" src="${escAttr(v.imageUrl)}" alt="" loading="lazy"></span>`
            } else if (v.swatchHex) {
              dot = `<span class="sai-srpfrd01__swatch-dot" style="background:${escAttr(v.swatchHex)}"></span>`
            } else if (facet.valueDisplayKind === 'image') {
              dot = `<span class="sai-srpfrd01__swatch-dot sai-srpfrd01__swatch-dot--placeholder"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-5-5L5 21"/></svg></span>`
            } else {
              dot = '<span class="sai-srpfrd01__swatch-dot"></span>'
            }
            html += `<label class="sai-srpfrd01__swatch" title="${escAttr(v.value)} (${v.count})">`
            html += `<input type="checkbox" data-filter-key="${escAttr(filterParam)}" data-filter-value="${escAttr(v.value)}"${isChecked ? ' checked' : ''}>`
            html += dot
            html += `<span class="sai-srpfrd01__swatch-label">${escAttr(v.text || v.value)}</span>`
            html += '</label>'
          }
          html += '</div>'
          return html
        }

        // Default: checkbox list (text or image-labeled values).
        let html = ''
        for (let j = 0; j < facet.values.length; j++) {
          const v = facet.values[j]
          const isChecked = valueInFilter(filters, filterParam, v.value)
          html += '<label class="sai-srpfrd01__facet-option">'
          html += `<input type="checkbox" data-filter-key="${escAttr(filterParam)}" data-filter-value="${escAttr(v.value)}"${isChecked ? ' checked' : ''}>`
          html += `<span>${escAttr(v.text || v.value)}</span>`
          html += `<span class="sai-srpfrd01__facet-count">(${v.count})</span>`
          html += '</label>'
        }
        return html
      }

      // Bind checkbox/toggle/swatch + range controls within `container`,
      // mutating `filters`. When `live`, each change applies immediately
      // (rewrite URL + re-search + track); otherwise it only stages the change
      // into `filters` (mobile overlay's deferred-apply model).
      _bindFacetControls(container, filters, live) {
        const self = this

        const checkboxes = container.querySelectorAll(
          '.sai-srpfrd01__facet-option input[type="checkbox"],' +
            ' .sai-srpfrd01__facet-toggle input[type="checkbox"],' +
            ' .sai-srpfrd01__swatch input[type="checkbox"]',
        )
        for (let k = 0; k < checkboxes.length; k++) {
          checkboxes[k].addEventListener('change', function () {
            const key = this.getAttribute('data-filter-key')
            const val = this.getAttribute('data-filter-value')
            toggleFilterValue(filters, key, val, this.checked)
            if (!live) return
            self._currentPage = 1
            self._writeUrlState()
            self._doSearch()
            self._track(`${FEATURE_SLUG}:filter_elem_clicked`, {
              filter_key: key,
              filter_value: val,
              action: this.checked ? 'add' : 'remove',
            })
          })
        }

        const rangeInputs = container.querySelectorAll('.sai-srpfrd01__range-input')

        function commitRange(key, group) {
          const inputs = group.querySelectorAll('.sai-srpfrd01__range-input')
          const gteInput = inputs[0]
          const lteInput = inputs[1]
          const sMin = Number(gteInput?.min)
          const sMax = Number(lteInput?.max)
          const gteVal = gteInput?.value?.trim()
          const lteVal = lteInput?.value?.trim()
          // A bound left at (or past) the facet's min/max is "no filter" — the
          // inputs stay visually filled, but a full-range selection sends nothing.
          if (gteVal && Number(gteVal) > sMin) filters[`${key}.gte`] = gteVal
          else delete filters[`${key}.gte`]
          if (lteVal && Number(lteVal) < sMax) filters[`${key}.lte`] = lteVal
          else delete filters[`${key}.lte`]
          return { gteVal, lteVal }
        }

        // Wire the dual-thumb sliders: keep both thumbs, the number inputs, and
        // the filled track in sync. A thumb at the extreme = "no bound", so it
        // clears that number input. Commit happens on thumb release (change).
        const rangeWraps = container.querySelectorAll('.sai-srpfrd01__range-wrap')
        for (let w = 0; w < rangeWraps.length; w++) {
          const wrap = rangeWraps[w]
          const slider = wrap.querySelector('.sai-srpfrd01__range-slider')
          const thumbs = wrap.querySelectorAll('.sai-srpfrd01__range-thumb')
          const numbers = wrap.querySelectorAll('.sai-srpfrd01__range-input')
          if (thumbs.length !== 2 || numbers.length !== 2) continue

          const gteThumb = thumbs[0]
          const lteThumb = thumbs[1]
          const gteNum = numbers[0]
          const lteNum = numbers[1]
          const sMin = Number(gteThumb.min)
          const sMax = Number(gteThumb.max)
          const span = sMax - sMin || 1
          const pct = (v) => ((v - sMin) / span) * 100

          function paint() {
            const lo = Number(gteThumb.value)
            const hi = Number(lteThumb.value)
            // The fill is the slider's ::after, positioned via these vars.
            slider.style.setProperty('--sai-srpfrd01-range-l', `${pct(lo)}%`)
            slider.style.setProperty('--sai-srpfrd01-range-r', `${100 - pct(hi)}%`)
            // Keep the lower thumb grabbable when both sit near the max.
            gteThumb.style.zIndex = lo >= sMax - span / 100 ? '3' : '2'
          }

          function syncFromThumbs(moved) {
            let lo = Number(gteThumb.value)
            let hi = Number(lteThumb.value)
            if (lo > hi) {
              if (moved === 'gte') {
                lo = hi
                gteThumb.value = String(lo)
              } else {
                hi = lo
                lteThumb.value = String(hi)
              }
            }
            // Keep the inputs filled with the thumb values (at-bound = no
            // filter is handled in commitRange, not by blanking the input).
            gteNum.value = String(lo)
            lteNum.value = String(hi)
            paint()
          }

          function syncFromNumbers() {
            const lo = gteNum.value.trim() === '' ? sMin : Number(gteNum.value)
            const hi = lteNum.value.trim() === '' ? sMax : Number(lteNum.value)
            if (Number.isFinite(lo)) gteThumb.value = String(Math.min(Math.max(lo, sMin), sMax))
            if (Number.isFinite(hi)) lteThumb.value = String(Math.min(Math.max(hi, sMin), sMax))
            paint()
          }

          gteThumb.addEventListener('input', () => syncFromThumbs('gte'))
          lteThumb.addEventListener('input', () => syncFromThumbs('lte'))
          gteNum.addEventListener('input', syncFromNumbers)
          lteNum.addEventListener('input', syncFromNumbers)

          const commitFromThumb = () => {
            const key = gteThumb.getAttribute('data-filter-key')
            const group = wrap.querySelector('.sai-srpfrd01__facet-range')
            const { gteVal, lteVal } = commitRange(key, group)
            if (!live) return
            self._currentPage = 1
            self._writeUrlState()
            self._doSearch()
            self._track(`${FEATURE_SLUG}:filter_elem_clicked`, {
              filter_key: key,
              filter_value: `${gteVal || '*'}-${lteVal || '*'}`,
              action: 'range',
            })
          }
          gteThumb.addEventListener('change', commitFromThumb)
          lteThumb.addEventListener('change', commitFromThumb)

          paint()
        }

        // No Apply button — committing the number inputs happens on `change`
        // (blur / Enter), matching the live-apply slider. Live mode searches
        // immediately; the mobile overlay just stages into pending filters and
        // commits everything via its footer Apply button.
        for (let k = 0; k < rangeInputs.length; k++) {
          rangeInputs[k].addEventListener('change', function () {
            const key = this.getAttribute('data-filter-key')
            const group = this.closest('.sai-srpfrd01__facet-range')
            const { gteVal, lteVal } = commitRange(key, group)
            if (!live) return
            self._currentPage = 1
            self._writeUrlState()
            self._doSearch()
            self._track(`${FEATURE_SLUG}:filter_elem_clicked`, {
              filter_key: key,
              filter_value: `${gteVal || '*'}-${lteVal || '*'}`,
              action: 'range',
            })
          })
        }
      }

      // ── Active filter tags ──

      _facetLabelForParam(filterParam) {
        const base = filterParam.replace(/\.(gte|lte)$/, '')
        for (const f of this._facets || []) {
          if (fieldToFilterParam(f.field) === base) return f.label
        }
        const seg = base.replace(/^filter\.v\./, '').replace(/^filter\.p\./, '')
        return seg.charAt(0).toUpperCase() + seg.slice(1).replace(/_/g, ' ')
      }

      _buildFallbackChips() {
        const filters = this._activeFilters
        const keys = Object.keys(filters)
        const chips = []
        const seen = new Set()
        for (let i = 0; i < keys.length; i++) {
          const key = keys[i]
          if (seen.has(key)) continue

          const gteMatch = key.match(/^(.+)\.gte$/)
          if (gteMatch) {
            const base = gteMatch[1]
            const gte = filters[key]
            const lte = filters[`${base}.lte`]
            seen.add(key)
            seen.add(`${base}.lte`)
            const label = this._facetLabelForParam(base)
            let text
            if (gte && lte) text = `${label}: ${gte} – ${lte}`
            else if (gte) text = `${label}: ${gte}+`
            else text = `${label}: up to ${lte}`
            chips.push({ key: base, value: null, text, isRange: true })
            continue
          }
          const lteMatch = key.match(/^(.+)\.lte$/)
          if (lteMatch && !seen.has(key)) {
            const base = lteMatch[1]
            seen.add(key)
            const label = this._facetLabelForParam(base)
            chips.push({
              key: base,
              value: null,
              text: `${label}: up to ${filters[key]}`,
              isRange: true,
            })
            continue
          }

          const label = this._facetLabelForParam(key)
          const val = filters[key]
          if (val === 'true') {
            chips.push({ key, value: null, text: label })
          } else {
            chips.push({ key, value: null, text: `${label}: ${val}` })
          }
        }
        return chips
      }

      _renderActiveFilters() {
        const els = this._els
        const self = this

        // Prefer the labeled chips from the facets endpoint
        // entries. The backend can return an empty appliedFilters even while
        // filters are active, so fall back to the raw active filters in that
        // case rather than showing nothing.
        //
        // Two appliedFilters shapes are supported:
        //   - { field, operator, values: [...] }        (current backend)
        //   - { field, label, value, displayValue }      (single-value spec)
        // A value's display text is enriched from the loaded facet values where
        // possible (e.g. discount "25" → "25% or more"); otherwise the raw value.
        const labelForValue = (field, raw) => {
          const facet = (this._facets || []).find((f) => f.field === field)
          const v = facet?.values?.find((x) => x.value === String(raw))
          return (v && (v.text || v.value)) || String(raw)
        }

        const apiChips = []
        if (Array.isArray(this._appliedFilters)) {
          for (const f of this._appliedFilters) {
            const key = fieldToFilterParam(f.field)
            if (Array.isArray(f.values)) {
              for (const raw of f.values) {
                const text = labelForValue(f.field, raw)
                if (text) apiChips.push({ key, value: String(raw), text })
              }
            } else {
              const text = f.displayValue || labelForValue(f.field, f.value) || f.label || ''
              if (text)
                apiChips.push({ key, value: f.value != null ? String(f.value) : null, text })
            }
          }
        }
        const chips = apiChips.length ? apiChips : this._buildFallbackChips()

        if (!chips.length) {
          els.activeFilters.hidden = true
          els.activeFilters.innerHTML = ''
          return
        }

        let html = ''
        for (let i = 0; i < chips.length; i++) {
          const c = chips[i]
          const valAttr = c.value != null ? ` data-filter-value="${escAttr(c.value)}"` : ''
          const rangeAttr = c.isRange ? ' data-range="1"' : ''
          html += `<button class="sai-srpfrd01__active-tag" data-filter-key="${escAttr(c.key)}"${valAttr}${rangeAttr}>${escAttr(c.text)} ×</button>`
        }
        html += '<button class="sai-srpfrd01__active-clear">Clear all</button>'

        els.activeFilters.innerHTML = html
        els.activeFilters.hidden = false

        const tags = els.activeFilters.querySelectorAll('.sai-srpfrd01__active-tag')
        for (let j = 0; j < tags.length; j++) {
          tags[j].addEventListener('click', function () {
            const fKey = this.getAttribute('data-filter-key')
            const fVal = this.getAttribute('data-filter-value')
            const isRange = this.hasAttribute('data-range')
            if (fVal != null) {
              toggleFilterValue(self._activeFilters, fKey, fVal, false)
              delete self._activeFilters[`${fKey}.gte`]
              delete self._activeFilters[`${fKey}.lte`]
            } else if (isRange) {
              delete self._activeFilters[`${fKey}.gte`]
              delete self._activeFilters[`${fKey}.lte`]
            } else {
              delete self._activeFilters[fKey]
            }
            self._currentPage = 1
            self._writeUrlState()
            self._doSearch()
          })
        }

        const clearBtn = els.activeFilters.querySelector('.sai-srpfrd01__active-clear')
        if (clearBtn) {
          clearBtn.addEventListener('click', () => {
            self._activeFilters = {}
            self._currentPage = 1
            self._writeUrlState()
            self._doSearch()
          })
        }
      }

      // ── Product card clicks ──

      _bindProductCards(container) {
        const self = this
        const cards = (container || this._els.grid).querySelectorAll('.sai-srpfrd01__product')
        for (let i = 0; i < cards.length; i++) {
          if (cards[i]._bound) continue
          cards[i]._bound = true
          ;((card) => {
            const handle = card.getAttribute('data-handle')
            const productUrl = `/products/${handle}`
            const navigateToPdp = card.getAttribute('data-navigate-to-pdp') === 'true'

            // Image + title — navigate to PDP if enabled
            if (navigateToPdp) {
              const imgWrap = card.querySelector('.sai-srpfrd01__product-img-wrap')
              const titleEl = card.querySelector('.sai-srpfrd01__product-title')
              function goToProduct(e) {
                e.stopPropagation()
                self._track(`${FEATURE_SLUG}:product_click`, { product_handle: handle })
                window.location.href = productUrl
              }
              if (imgWrap) {
                imgWrap.style.cursor = 'pointer'
                imgWrap.addEventListener('click', goToProduct)
              }
              if (titleEl) {
                titleEl.style.cursor = 'pointer'
                titleEl.addEventListener('click', goToProduct)
              }
            }

            // CTA (quick-view + single-variant ATC) is handled by the shared
            // spectrum-quick-add.js via global click delegation on the
            // data-spectrum-quick-add-trigger / data-spectrum-direct-atc
            // attributes emitted in buildCard — no per-card handler here.
          })(cards[i])
        }
      }

      _buildMiniPdpPayload(product) {
        return {
          productId: product.id,
          title: product.title,
          url: product.url || `/products/${product.handle || ''}`,
          description: product.body_html || product.description || '',
          images: (product.images || [])
            .map((img) => (typeof img === 'string' ? img : img.src || ''))
            .filter(Boolean),
          variants: (product.variants || []).map((v) => ({
            id: v.id,
            title:
              v.title || [v.option1, v.option2, v.option3].filter(Boolean).join(' / ') || 'Default',
            price: formatMoney(v.price || product.price),
            compareAtPrice:
              v.compare_at_price && v.compare_at_price > (v.price || 0)
                ? formatMoney(v.compare_at_price)
                : null,
            available: v.available !== false,
          })),
        }
      }

      _fetchFullProductForPdp(handle, payload) {
        const api = productsApi()
        if (!api || !handle) return
        api
          .getByHandle(handle)
          .then((full) => {
            if (!full || !this._miniPdp) return
            const fullImages = (full.images || [])
              .map((img) => (typeof img === 'string' ? img : img.src || ''))
              .filter(Boolean)
            if (fullImages.length > payload.images.length) {
              // Re-open with full images if the modal is still showing this product
              // We don't re-open, just update the gallery in-place is handled by initMiniPdp
            }
          })
          .catch(() => {})
      }

      async _cardAddToCart(btn, product) {
        const variantId = btn.getAttribute('data-variant-id')
        if (!variantId) return

        this._track(`${FEATURE_SLUG}:add_to_cart`, {
          product_id: product.id,
          variant_id: variantId,
          quantity: 1,
          source: 'instant',
        })

        btn.dataset.state = 'loading'
        btn.disabled = true
        btn.setAttribute('aria-busy', 'true')
        btn.textContent = 'Adding...'

        try {
          const res = await addVariantToCart(Number(variantId), 1)
          btn.dataset.state = 'success'
          btn.textContent = 'Added!'
          btn.removeAttribute('aria-busy')

          notifyCartUpdate({
            variantId,
            productId: product.id,
            response: res,
          })

          this._track(`${FEATURE_SLUG}:added_to_cart`, {
            product_id: product.id,
            variant_id: variantId,
            quantity: 1,
          })

          setTimeout(() => {
            btn.dataset.state = 'idle'
            btn.disabled = false
            btn.textContent = this._cardSettings.ctaLabel
          }, 4000)
        } catch (err) {
          btn.dataset.state = 'idle'
          btn.disabled = false
          btn.removeAttribute('aria-busy')
          btn.textContent = this._cardSettings.ctaLabel
        }
      }

      // ── Pagination ──

      _renderPagination() {
        const els = this._els
        const style = this._activePaginationStyle()

        if (style === 'infinite_scroll') {
          this._setupInfiniteScroll()
          return
        }

        if (this._totalPages <= 1) {
          els.pagination.hidden = true
          return
        }

        const self = this
        const page = this._currentPage
        const total = this._totalPages
        let html = ''

        html += `<button class="sai-srpfrd01__page-btn" data-page="${page - 1}"${page <= 1 ? ' disabled' : ''} aria-label="Previous page">`
        html +=
          '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="15 18 9 12 15 6"/></svg>'
        html += '</button>'

        const pages = this._getPageRange(page, total)
        for (let i = 0; i < pages.length; i++) {
          if (pages[i] === '...') {
            html += '<span class="sai-srpfrd01__page-ellipsis">…</span>'
          } else {
            const p = pages[i]
            html += `<button class="sai-srpfrd01__page-btn" data-page="${p}"${p === page ? ' aria-current="page"' : ''}>${p}</button>`
          }
        }

        html += `<button class="sai-srpfrd01__page-btn" data-page="${page + 1}"${page >= total ? ' disabled' : ''} aria-label="Next page">`
        html +=
          '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 18 15 12 9 6"/></svg>'
        html += '</button>'

        els.pagination.innerHTML = html
        els.pagination.hidden = false

        const btns = els.pagination.querySelectorAll('.sai-srpfrd01__page-btn')
        for (let j = 0; j < btns.length; j++) {
          btns[j].addEventListener('click', function () {
            const p = Number(this.getAttribute('data-page'))
            if (p >= 1 && p <= self._totalPages) {
              self._currentPage = p
              self._writeUrlState()
              self._doSearch()
              self.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }
          })
        }
      }

      _getPageRange(current, total) {
        if (total <= 7) {
          const r = []
          for (let i = 1; i <= total; i++) r.push(i)
          return r
        }

        const range = []
        range.push(1)

        if (current > 3) range.push('...')

        const start = Math.max(2, current - 1)
        const end = Math.min(total - 1, current + 1)

        for (let j = start; j <= end; j++) range.push(j)

        if (current < total - 2) range.push('...')

        range.push(total)
        return range
      }

      _setupInfiniteScroll() {
        const els = this._els
        if (this._infiniteObserver) this._infiniteObserver.disconnect()
        if (this._scrollHandler) {
          window.removeEventListener('scroll', this._scrollHandler)
          this._scrollHandler = null
        }

        if (this._currentPage >= this._totalPages) {
          els.pagination.innerHTML = ''
          els.pagination.hidden = true
          return
        }

        els.pagination.innerHTML =
          '<div class="sai-srpfrd01__infinite-sentinel"></div>' +
          '<div class="sai-srpfrd01__infinite-loader" hidden>' +
          '<svg class="sai-srpfrd01__spinner" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg>' +
          '</div>'
        els.pagination.hidden = false
        let loading = false
        const self = this

        function loadNext() {
          if (loading || self._currentPage >= self._totalPages) return
          loading = true
          const loader = els.pagination.querySelector('.sai-srpfrd01__infinite-loader')
          if (loader) loader.hidden = false
          self._currentPage++
          self._writeUrlState({ replace: true })
          self._doSearch().finally(() => {
            loading = false
            const currentLoader = els.pagination.querySelector('.sai-srpfrd01__infinite-loader')
            if (currentLoader) currentLoader.hidden = true
          })
        }

        const sentinel = els.pagination.querySelector('.sai-srpfrd01__infinite-sentinel')

        if (typeof IntersectionObserver !== 'undefined') {
          this._infiniteObserver = new IntersectionObserver(
            (entries) => {
              if (entries[0].isIntersecting) loadNext()
            },
            { rootMargin: '200px' },
          )
          this._infiniteObserver.observe(sentinel)
        }

        this._scrollHandler = () => {
          if (loading) return
          const rect = sentinel.getBoundingClientRect()
          if (rect.top < window.innerHeight + 200) loadNext()
        }
        window.addEventListener('scroll', this._scrollHandler, { passive: true })
      }

      // ── Mobile Filter Overlay ──

      _openFilterOverlay() {
        if (!this._facets || !this._facets.length) return
        this._pendingFilters = Object.assign({}, this._activeFilters)
        this._renderMobileFilterGroups()
        this._els.filterOverlay.hidden = false
        document.body.style.overflow = 'hidden'
      }

      _closeFilterOverlay() {
        this._els.filterOverlay.hidden = true
        document.body.style.overflow = ''
      }

      _renderMobileFilterGroups() {
        const els = this._els
        if (!this._facets || !this._facets.length) {
          els.filterGroups.innerHTML = ''
          els.filterValues.innerHTML = ''
          return
        }

        const self = this
        let html = ''
        for (let i = 0; i < this._facets.length; i++) {
          const facet = this._facets[i]
          html += `<button class="sai-srpfrd01__filter-group-btn" data-group="${i}"${i === 0 ? ' aria-selected="true"' : ''}>${escAttr(facet.label)}</button>`
        }

        els.filterGroups.innerHTML = html
        this._activeMobileGroup = 0
        this._renderMobileFilterValues()

        const btns = els.filterGroups.querySelectorAll('.sai-srpfrd01__filter-group-btn')
        for (let j = 0; j < btns.length; j++) {
          btns[j].addEventListener('click', function () {
            const prev = els.filterGroups.querySelector('[aria-selected="true"]')
            if (prev) prev.setAttribute('aria-selected', 'false')
            this.setAttribute('aria-selected', 'true')
            self._activeMobileGroup = Number(this.getAttribute('data-group'))
            self._renderMobileFilterValues()
          })
        }
      }

      _renderMobileFilterValues() {
        const els = this._els
        if (!this._facets || this._activeMobileGroup == null) return

        const facet = this._facets[this._activeMobileGroup]
        if (!facet) {
          els.filterValues.innerHTML = ''
          return
        }

        const filterParam = fieldToFilterParam(facet.field)
        els.filterValues.innerHTML = `<div class="sai-srpfrd01__facet-body">${this._facetBodyHtml(
          facet,
          filterParam,
          this._pendingFilters,
          false,
        )}</div>`
        this._bindFacetControls(els.filterValues, this._pendingFilters, false)
      }

      // ── Mobile Sort Sheet ──

      _openSortSheet() {
        const els = this._els
        const self = this
        const options = this._sortOptions || SORT_OPTIONS
        let html = ''
        for (let i = 0; i < options.length; i++) {
          const opt = options[i]
          const isSelected = this._currentSort === opt.value
          html += `<button class="sai-srpfrd01__sort-option" data-sort="${escAttr(opt.value)}"${isSelected ? ' aria-selected="true"' : ''}>${opt.label}</button>`
        }
        els.sortSheetOptions.innerHTML = html
        els.sortSheet.hidden = false
        document.body.style.overflow = 'hidden'

        const btns = els.sortSheetOptions.querySelectorAll('.sai-srpfrd01__sort-option')
        for (let j = 0; j < btns.length; j++) {
          btns[j].addEventListener('click', function () {
            self._currentSort = this.getAttribute('data-sort')
            self._currentPage = 1
            if (els.sortSelect) els.sortSelect.value = self._currentSort
            self._closeSortSheet()
            self._writeUrlState()
            self._doSearch()
            self._track(`${FEATURE_SLUG}:sort_elem_clicked`, { sort: self._currentSort })
          })
        }
      }

      _closeSortSheet() {
        this._els.sortSheet.hidden = true
        document.body.style.overflow = ''
      }

      // ── Variant Application ──

      applyVariant(content) {
        if (!this._els) return
        this._resultsPerPage = content.results_per_page || 10
        this._paginationStyleDesktop = content.pagination_style_desktop || 'numbered'
        this._paginationStyleMobile = content.pagination_style_mobile || 'infinite_scroll'
        this._showFacetFilters = content.show_facet_filters !== false
        this._showSort = content.show_sort !== false
        this._gridColumnsDesktop = content.grid_columns_desktop || 4
        this._gridColumnsMobile = content.grid_columns_mobile || 2

        // Card settings
        this._cardSettings = {
          cardHoverShadow: content.card_hover_shadow !== false,
          cardHoverSwapImage: content.card_hover_swap_image === true,
          showCta: content.show_cta !== false,
          enableMiniPdp: content.enable_mini_pdp !== false,
          ctaLabel: content.cta_label || 'Add To Cart',
          soldOutLabel: content.sold_out_label || 'Sold out',
          enableNavigateToPdp: content.enable_navigate_to_pdp !== false,
          showCurrentPrice: content.show_current_price !== false,
          showMrp: content.show_mrp !== false,
          showDiscountBadge: content.show_discount_badge !== false,
          discountFormat: content.discount_format || 'percent',
        }

        const ar = content.image_aspect_ratio || '1/1'
        this.style.setProperty('--sai-srpfrd01-img-ratio', ar)
        const fit = content.image_object_fit || 'cover'
        this.style.setProperty('--sai-srpfrd01-img-fit', fit)

        this._applyGridColumns()
        this._applyResponsive()
        // applyVariant runs after connectedCallback's _readUrlState(), so it must
        // not clobber the page parsed from ?page=N. Numbered pagination honours the
        // URL page (back-from-PDP lands on the page the visitor left); infinite
        // scroll always restarts at the top.
        const urlPage = Number(new URLSearchParams(window.location.search).get('page')) || 1
        this._currentPage = this._activePaginationStyle() === 'numbered' ? urlPage : 1
        this._doSearch()
      }
    }

    customElements.define(TAG, SaiSearchPage)
  }

  // ── Bind all containers ──

  function bindAllContainers() {
    const api = window.__spectrumAi?.snippet
    const containers = document.querySelectorAll(
      `[data-spectrum-instance-id][data-spectrum-snippet-id="${SNIPPET_ID}"]`,
    )

    if (!api?.bind) return

    for (let i = 0; i < containers.length; i++) {
      const node = containers[i]
      const handles = api.bind(node, (ctx) => {
        if (!ctx.variant?.content) return
        const root = node.querySelector(TAG)
        if (root && typeof root.applyVariant === 'function') {
          root.applyVariant(ctx.variant.content)
        }
      })
      const root = node.querySelector(TAG)
      if (root && handles?.track && typeof root.setAnalytics === 'function') {
        root.setAnalytics(handles.track)
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindAllContainers, { once: true })
  } else {
    bindAllContainers()
  }
})()
